/**
 * Menu stylesheet. Procedural: no images, no fonts, no assets of any kind —
 * every surface here is a gradient, a border or a piece of text.
 *
 * It follows the same rules as src/render/hud.ts, because the two are on screen
 * back to back and any difference between them reads as a bug:
 *
 *  - one type scale, one weight ramp, uppercase tracked labels;
 *  - `font-variant-numeric: tabular-nums` on everything, so a slider value
 *    changing from 110 to 99 does not shift the column it sits in;
 *  - nothing centred by eye — rows are grids with fixed value columns, so the
 *    values form a hard right edge down the whole panel.
 *
 * The accent colour is a CSS variable driven by the selected team, so choosing
 * orange in match setup re-tints the entire front end. That is the one piece of
 * chrome that exists purely to make a setting feel like it took effect.
 */

/** Layout constants shared between the sheet and the markup. */
export const MENU_Z_INDEX = 20;

export const MENU_STYLE = `
.rw-menu {
  position: absolute;
  inset: 0;
  z-index: ${MENU_Z_INDEX};
  display: flex;
  user-select: none;
  font-family: 'Inter', 'SF Pro Display', 'Segoe UI Variable Display', system-ui,
    -apple-system, 'Helvetica Neue', Arial, sans-serif;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  color: #eef4ff;
  -webkit-font-smoothing: antialiased;
  --rw-accent: #2a7dff;
  --rw-accent-dim: rgba(42, 125, 255, 0.16);
  --rw-line: rgba(255, 255, 255, 0.07);
  --rw-muted: rgba(186, 204, 232, 0.62);
}
.rw-menu * { box-sizing: border-box; font-variant-numeric: tabular-nums; }
.rw-menu[data-team='1'] {
  --rw-accent: #ff8a1f;
  --rw-accent-dim: rgba(255, 138, 31, 0.16);
}
.rw-menu[hidden] { display: none; }

/* Backdrop: a dark wash, a cold key light from the accent side, and a very
   faint pitch-line rake. All three are gradients — nothing is loaded. */
.rw-menu-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(120% 90% at 12% 0%, var(--rw-accent-dim) 0%, rgba(0,0,0,0) 55%),
    radial-gradient(90% 70% at 90% 100%, rgba(120, 160, 230, 0.08) 0%, rgba(0,0,0,0) 60%),
    linear-gradient(160deg, rgba(6, 9, 16, 0.94) 0%, rgba(3, 5, 10, 0.97) 100%);
  backdrop-filter: blur(9px) saturate(0.85);
}
.rw-menu-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background: repeating-linear-gradient(
    100deg,
    rgba(255, 255, 255, 0.022) 0px,
    rgba(255, 255, 255, 0.022) 1px,
    rgba(0, 0, 0, 0) 1px,
    rgba(0, 0, 0, 0) 84px
  );
  -webkit-mask-image: radial-gradient(75% 70% at 30% 40%, #000 0%, rgba(0,0,0,0) 100%);
  mask-image: radial-gradient(75% 70% at 30% 40%, #000 0%, rgba(0,0,0,0) 100%);
}

.rw-menu-frame {
  position: relative;
  flex: 1;
  display: grid;
  grid-template-rows: auto 1fr auto;
  padding: 34px clamp(28px, 7vw, 96px) 26px;
  min-height: 0;
}

/* --- brand ------------------------------------------------------------- */

.rw-brand { display: flex; align-items: center; gap: 14px; }
.rw-brand-mark {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: #f2f7ff;
}
.rw-brand-mark span { color: var(--rw-accent); }
.rw-brand-rule { flex: 1; height: 1px; background: var(--rw-line); }
.rw-brand-build {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: rgba(150, 172, 205, 0.55);
}

/* --- panel ------------------------------------------------------------- */

/* Centred vertically, but bounded by its grid row: the controls screen has
   sixteen rows and WILL exceed a short window. Without the max-height the panel
   sizes to content and rides straight through the header and the legend. */
.rw-panel {
  align-self: center;
  width: 100%;
  max-width: 660px;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 22px 0;
}
.rw-crumbs, .rw-title, .rw-sub, .rw-extra, .rw-note { flex: none; }
.rw-crumbs {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
  margin-bottom: 10px;
  min-height: 12px;
}
.rw-crumbs b { color: var(--rw-accent); font-weight: 650; }
.rw-title {
  margin: 0;
  font-size: clamp(38px, 5.2vw, 58px);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 0.94;
}
.rw-sub {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rw-muted);
  min-height: 14px;
}

/* An account result standing in for the fixed subtitle: what the last request
   did, and whether it worked. Deliberately louder than the subtitle it
   replaces — larger, mixed case and tighter letter-spacing — because it is a
   sentence to read rather than a label to glance at, and because uppercase
   tracked-out text is where a reason like "that name is taken" goes to hide. */
.rw-sub.bad,
.rw-sub.good {
  font-size: 13px;
  letter-spacing: 0.01em;
  text-transform: none;
}
.rw-sub.bad { color: #ff9d7a; }
.rw-sub.good { color: #7fd8a4; }

.rw-rows {
  margin-top: 22px;
  border-top: 1px solid var(--rw-line);
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.16) transparent;
}
.rw-rows:empty { display: none; }

/* --- rows -------------------------------------------------------------- */

.rw-row {
  position: relative;
  display: grid;
  align-items: center;
  gap: 16px;
  min-height: 46px;
  padding: 0 14px 0 18px;
  border-bottom: 1px solid var(--rw-line);
  cursor: pointer;
  transition: background 110ms linear, color 110ms linear;
}
.rw-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--rw-accent);
  transform: scaleY(0);
  transition: transform 130ms cubic-bezier(0.16, 1, 0.3, 1);
}
.rw-row.on {
  background: linear-gradient(90deg, var(--rw-accent-dim) 0%, rgba(0, 0, 0, 0) 78%);
}
.rw-row.on::before { transform: scaleY(1); }
.rw-row.locked { cursor: not-allowed; }
.rw-row:hover:not(.on) { background: rgba(255, 255, 255, 0.028); }

.rw-row-label {
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--rw-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rw-row.on .rw-row-label { color: #f4f8ff; }
.rw-row.locked .rw-row-label { color: rgba(150, 172, 205, 0.34); }

/* Emphasised actions (Play, Start Match, Resume) carry the type weight of a
   heading rather than a row, so the primary action is never ambiguous. */
.rw-row.hero { min-height: 58px; }
.rw-row.hero .rw-row-label {
  font-size: 20px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: none;
  color: #f4f8ff;
}
.rw-row.hero.on .rw-row-label { color: #fff; }

.rw-row-value {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 650;
  letter-spacing: 0.01em;
  color: #e9f0ff;
  white-space: nowrap;
}
.rw-row.locked .rw-row-value { color: rgba(190, 208, 235, 0.34); }
.rw-row-value b {
  min-width: 108px;
  text-align: right;
  font-weight: 650;
}
.rw-chev {
  width: 11px;
  font-size: 13px;
  line-height: 1;
  text-align: center;
  color: var(--rw-accent);
  opacity: 0;
  transition: opacity 110ms linear;
}
.rw-row.on:not(.locked) .rw-chev { opacity: 1; }

.rw-lock {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  padding: 3px 6px 2px;
  border: 1px solid rgba(255, 196, 92, 0.4);
  color: rgba(255, 202, 110, 0.9);
  border-radius: 2px;
}

/* Slider: a 2px rail with a hard-edged fill, plus a tick at the default. */
.rw-track {
  position: relative;
  width: 168px;
  height: 2px;
  background: rgba(255, 255, 255, 0.12);
}
.rw-track i {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--rw-accent);
  display: block;
}
.rw-track u {
  position: absolute;
  top: -3px;
  width: 1px;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
}

/* Key caps. Fixed min-width so the two slots line up down the column. */
.rw-cap {
  min-width: 62px;
  padding: 5px 8px 4px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #dce6f8;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
.rw-cap.empty { color: rgba(160, 180, 210, 0.35); }
.rw-row.on .rw-cap.armed {
  border-color: var(--rw-accent);
  color: #fff;
  background: var(--rw-accent-dim);
}

.rw-note {
  margin-top: 14px;
  min-height: 32px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.5;
  color: rgba(178, 198, 228, 0.72);
}
.rw-note.warn { color: rgba(255, 202, 110, 0.92); }

/* --- extra blocks (scoreboard, match card) ----------------------------- */

.rw-extra { margin-top: 26px; }
.rw-extra:empty { display: none; }

.rw-final {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 18px;
  padding: 20px 0 22px;
  border-top: 1px solid var(--rw-line);
  border-bottom: 1px solid var(--rw-line);
}
.rw-final-team { display: flex; flex-direction: column; gap: 6px; }
.rw-final-team.right { align-items: flex-end; }
.rw-final-name {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}
.rw-final-score {
  font-size: 62px;
  font-weight: 800;
  letter-spacing: -0.05em;
  line-height: 0.86;
}
.rw-final-dash { color: rgba(150, 172, 205, 0.4); font-size: 22px; font-weight: 300; }
.rw-final-tag {
  margin-top: 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--rw-muted);
}

.rw-goals { margin-top: 12px; max-height: 132px; overflow-y: auto; }
.rw-goal {
  display: grid;
  grid-template-columns: 58px 1fr auto;
  gap: 12px;
  padding: 5px 0;
  font-size: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.rw-goal-clock { color: rgba(150, 172, 205, 0.7); font-weight: 650; }
.rw-goal-team { font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; font-size: 11px; }
.rw-goal-speed { color: rgba(178, 198, 228, 0.66); }

.rw-card {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--rw-line);
  border: 1px solid var(--rw-line);
}
.rw-card-cell { background: rgba(8, 11, 18, 0.72); padding: 14px 16px; }
.rw-card-key {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-card-val { margin-top: 6px; font-size: 17px; font-weight: 700; letter-spacing: -0.01em; }

/* --- ranked ------------------------------------------------------------- */

/* The tier colour arrives as an inline --rw-tier custom property per card (see
   ./ranked-panels), so the palette lives in src/game/rating.ts with the bands
   it belongs to rather than being duplicated as seven classes here. */
.rw-rank-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 1px;
  background: var(--rw-line);
  border: 1px solid var(--rw-line);
}
.rw-rank-card {
  background: rgba(8, 11, 18, 0.72);
  padding: 14px 16px 16px;
  border-top: 3px solid var(--rw-tier, var(--rw-line));
}
.rw-rank-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.rw-rank-mode {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-rank-rating { font-size: 26px; font-weight: 800; letter-spacing: -0.03em; }
.rw-rank-label {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--rw-tier, #eef4ff);
}
.rw-rank-bar { margin-top: 10px; height: 3px; background: rgba(255, 255, 255, 0.09); }
.rw-rank-bar i {
  display: block;
  height: 100%;
  width: 100%;
  background: var(--rw-tier, var(--rw-accent));
  transform-origin: left center;
}
.rw-rank-record {
  margin-top: 9px;
  display: flex;
  gap: 12px;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.1em;
  color: rgba(178, 198, 228, 0.7);
}
.rw-rank-rate { margin-left: auto; color: #eef4ff; }
.rw-rank-title {
  margin: 18px 0 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-rank-empty {
  padding: 14px 16px;
  border: 1px solid var(--rw-line);
  background: rgba(8, 11, 18, 0.62);
  font-size: 12px;
  color: rgba(178, 198, 228, 0.75);
}
.rw-rank-card.rw-rank-empty { border-top-color: var(--rw-line); }
.rw-rank-note { margin-top: 6px; font-size: 12px; color: rgba(178, 198, 228, 0.7); }

/* Board and history share a column grid so the two tables line up down the
   panel; the value columns are fixed so the numbers form a hard right edge. */
.rw-board, .rw-hist { border: 1px solid var(--rw-line); }
.rw-board-row, .rw-board-head, .rw-hist-row {
  display: grid;
  grid-template-columns: 30px 8px 1fr 58px 62px;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--rw-line);
  font-size: 12px;
}
.rw-board-row:last-child, .rw-hist-row:last-child { border-bottom: none; }
.rw-board-head {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.6);
}
.rw-board-rank { color: rgba(150, 172, 205, 0.7); }
.rw-board-dot { width: 8px; height: 8px; border-radius: 50%; }
.rw-board-name { font-weight: 650; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-board-played, .rw-board-rating { text-align: right; }
.rw-board-rating { font-weight: 700; }
.rw-board-row.mine { background: var(--rw-accent-dim); }
.rw-board-row.mine .rw-board-name { color: var(--rw-accent); }

.rw-hist-row { grid-template-columns: 52px 1fr 58px 46px 52px; }
.rw-hist-outcome { font-weight: 700; letter-spacing: 0.06em; }
.rw-hist-row.win .rw-hist-outcome { color: #7fd8a4; }
.rw-hist-row.loss .rw-hist-outcome { color: #ff9d7a; }
.rw-hist-row.draw .rw-hist-outcome { color: rgba(198, 214, 240, 0.75); }
/* An unranked match is shown greyed rather than hidden — see historyMarkup. */
.rw-hist-row.unranked { color: rgba(150, 172, 205, 0.45); }
.rw-hist-row.unranked .rw-hist-outcome { color: rgba(150, 172, 205, 0.55); }
.rw-hist-mode { color: rgba(178, 198, 228, 0.7); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-hist-score, .rw-hist-goals, .rw-hist-change { text-align: right; }
.rw-hist-change { font-weight: 700; }

/* A sentence under a card. Mixed case and untracked, unlike every label around
   it, because it is a thing to read rather than a thing to glance at — the
   same distinction the account status line makes above. */
.rw-note-line {
  margin-top: 12px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(198, 214, 240, 0.72);
}

/* --- training ----------------------------------------------------------- */

/* The training screens are the only two-column layout in the front end, and
   they have to be: twelve rows of controls and five charts do not stack inside
   660px on any window worth using. The panel becomes a grid and the children
   are placed by area, so nothing about the markup changes — the same elements
   are laid out differently, which is the whole reason the view has no
   per-screen branches in it. */
.rw-menu[data-screen='training'] .rw-panel,
.rw-menu[data-screen='checkpoints'] .rw-panel,
.rw-menu[data-screen='checkpoint'] .rw-panel {
  max-width: 1180px;
  display: grid;
  grid-template-columns: minmax(300px, 400px) 1fr;
  grid-template-areas:
    'crumbs crumbs'
    'title  title'
    'sub    sub'
    'rows   extra'
    'note   note';
  align-content: start;
  column-gap: 30px;
}
.rw-menu[data-screen='training'] .rw-crumbs,
.rw-menu[data-screen='checkpoints'] .rw-crumbs,
.rw-menu[data-screen='checkpoint'] .rw-crumbs { grid-area: crumbs; }
.rw-menu[data-screen='training'] .rw-title,
.rw-menu[data-screen='checkpoints'] .rw-title,
.rw-menu[data-screen='checkpoint'] .rw-title { grid-area: title; font-size: clamp(30px, 3.4vw, 42px); }
.rw-menu[data-screen='training'] .rw-sub,
.rw-menu[data-screen='checkpoints'] .rw-sub,
.rw-menu[data-screen='checkpoint'] .rw-sub { grid-area: sub; }
.rw-menu[data-screen='training'] .rw-rows,
.rw-menu[data-screen='checkpoints'] .rw-rows,
.rw-menu[data-screen='checkpoint'] .rw-rows { grid-area: rows; }
.rw-menu[data-screen='training'] .rw-extra,
.rw-menu[data-screen='checkpoints'] .rw-extra,
.rw-menu[data-screen='checkpoint'] .rw-extra {
  grid-area: extra;
  margin-top: 22px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.16) transparent;
}
.rw-menu[data-screen='training'] .rw-note,
.rw-menu[data-screen='checkpoints'] .rw-note,
.rw-menu[data-screen='checkpoint'] .rw-note { grid-area: note; }

/* A live match behind the menu is the point of watch-live, so the backdrop
   gets out of its way — but only then, and only partly: the rows still have to
   be legible over a moving arena. */
.rw-menu[data-watching='on'] .rw-menu-bg { opacity: 0.62; }
.rw-menu[data-watching='on'] .rw-panel {
  background: linear-gradient(90deg, rgba(4, 7, 12, 0.86) 0%, rgba(4, 7, 12, 0.62) 100%);
  padding-left: 18px;
  padding-right: 18px;
  margin-left: -18px;
}

.rw-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 1px;
  background: var(--rw-line);
  border: 1px solid var(--rw-line);
}
.rw-stat { background: rgba(8, 11, 18, 0.78); padding: 9px 11px; }
.rw-stat-key {
  font-size: 9px;
  font-weight: 650;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-stat-val { margin-top: 4px; font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }

/* Steps collected within the current iteration. Four workers report four
   times, so this moves in visible steps rather than sweeping. */
.rw-progress { margin-top: 8px; height: 2px; background: rgba(255, 255, 255, 0.1); }
.rw-progress i {
  display: block;
  height: 100%;
  background: var(--rw-accent);
  transition: width 160ms linear;
}

.rw-charts {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
}
.rw-chart {
  padding: 8px 10px 6px;
  border: 1px solid var(--rw-line);
  background: rgba(8, 11, 18, 0.62);
}
.rw-chart-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.rw-chart-name { flex: 1; color: rgba(150, 172, 205, 0.7); }
.rw-read { font-size: 12px; letter-spacing: 0.02em; text-transform: none; }
.rw-read.a { color: var(--rw-accent); }
.rw-read.b { color: rgba(190, 208, 235, 0.72); }

/* preserveAspectRatio="none" on the svg: the viewBox is in fixed user units
   and the box it lands in is whatever the grid gives it, so the line is
   stretched rather than letterboxed. Nothing in a sparkline needs a square
   aspect, and letterboxing would leave dead bands inside every card. */
.rw-chart-svg { display: block; width: 100%; height: 62px; margin-top: 4px; }
.rw-spark { fill: none; stroke-width: 1.4; vector-effect: non-scaling-stroke; }
.rw-spark.a { stroke: var(--rw-accent); }
.rw-spark.b { stroke: rgba(190, 208, 235, 0.5); stroke-dasharray: 3 2; }
.rw-zero { stroke: rgba(255, 255, 255, 0.12); stroke-width: 1; vector-effect: non-scaling-stroke; }
.rw-chart-axis {
  margin-top: 2px;
  font-size: 9px;
  letter-spacing: 0.1em;
  color: rgba(150, 172, 205, 0.45);
}

.rw-terms { margin-top: 14px; border-top: 1px solid var(--rw-line); padding-top: 10px; }
.rw-terms-head {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
  margin-bottom: 6px;
}
.rw-term {
  display: grid;
  grid-template-columns: 170px 1fr 74px;
  align-items: center;
  gap: 10px;
  padding: 2px 0;
  font-size: 11px;
}
.rw-term-name { color: rgba(178, 198, 228, 0.78); }
.rw-term-bar { position: relative; height: 4px; background: rgba(255, 255, 255, 0.06); }
.rw-term-bar i { position: absolute; left: 0; top: 0; bottom: 0; background: var(--rw-accent); }
.rw-term-bar.neg i { background: rgba(255, 122, 122, 0.75); }
.rw-term-val { text-align: right; color: #e9f0ff; font-weight: 650; }

.rw-active {
  margin-top: 12px;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: rgba(178, 198, 228, 0.78);
}
.rw-active b { color: var(--rw-accent); font-weight: 700; }
.rw-active.none { color: rgba(255, 202, 110, 0.8); }

@media (max-width: 1000px) {
  /* One column again. The charts are still worth having; the rows are simply
     above them, and the panel scrolls. */
  .rw-menu[data-screen='training'] .rw-panel,
  .rw-menu[data-screen='checkpoints'] .rw-panel,
  .rw-menu[data-screen='checkpoint'] .rw-panel {
    grid-template-columns: 1fr;
    grid-template-areas: 'crumbs' 'title' 'sub' 'rows' 'extra' 'note';
    max-width: 660px;
  }
}

/* --- progression: shop, pass, challenges ------------------------------- */

/* The fourth two-column layout, for the same reason as the training and
   multiplayer ones: ten shop cards, or a nine-tier track, do not stack beside
   their rows inside 660px. */
.rw-menu[data-screen='shop'] .rw-panel,
.rw-menu[data-screen='rocketpass'] .rw-panel,
.rw-menu[data-screen='challenges'] .rw-panel,
.rw-menu[data-screen='ranked'] .rw-panel {
  max-width: 1140px;
  display: grid;
  grid-template-columns: minmax(300px, 420px) 1fr;
  grid-template-areas:
    'crumbs crumbs'
    'title  title'
    'sub    sub'
    'rows   extra'
    'note   note';
  align-content: start;
  column-gap: 30px;
}
.rw-menu[data-screen='shop'] .rw-crumbs,
.rw-menu[data-screen='rocketpass'] .rw-crumbs,
.rw-menu[data-screen='challenges'] .rw-crumbs,
.rw-menu[data-screen='ranked'] .rw-crumbs { grid-area: crumbs; }
.rw-menu[data-screen='shop'] .rw-title,
.rw-menu[data-screen='rocketpass'] .rw-title,
.rw-menu[data-screen='challenges'] .rw-title,
.rw-menu[data-screen='ranked'] .rw-title { grid-area: title; font-size: clamp(30px, 3.4vw, 42px); }
.rw-menu[data-screen='shop'] .rw-sub,
.rw-menu[data-screen='rocketpass'] .rw-sub,
.rw-menu[data-screen='challenges'] .rw-sub,
.rw-menu[data-screen='ranked'] .rw-sub { grid-area: sub; }
.rw-menu[data-screen='shop'] .rw-rows,
.rw-menu[data-screen='rocketpass'] .rw-rows,
.rw-menu[data-screen='challenges'] .rw-rows,
.rw-menu[data-screen='ranked'] .rw-rows { grid-area: rows; }
.rw-menu[data-screen='shop'] .rw-extra,
.rw-menu[data-screen='rocketpass'] .rw-extra,
.rw-menu[data-screen='challenges'] .rw-extra,
.rw-menu[data-screen='ranked'] .rw-extra {
  grid-area: extra;
  margin-top: 22px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.16) transparent;
}
.rw-menu[data-screen='shop'] .rw-note,
.rw-menu[data-screen='rocketpass'] .rw-note,
.rw-menu[data-screen='challenges'] .rw-note,
.rw-menu[data-screen='ranked'] .rw-note { grid-area: note; }

/* A value row: a label that may carry a counter bar, and a right-aligned
   read-only value. No chevrons — there is no adjacent price. */
.rw-row-stack { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.rw-mini { height: 2px; background: rgba(255, 255, 255, 0.1); }
.rw-mini i { display: block; height: 100%; background: var(--rw-accent); }
.rw-badge {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 3px 6px 2px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 2px;
  color: rgba(190, 208, 235, 0.78);
}
.rw-row.on .rw-badge { border-color: var(--rw-accent); color: #f2f7ff; }
/* A rarity-toned badge keeps its own colour whether or not the row is focused:
   the whole use of a rarity on a forty-row list is that you can scan it. */
.rw-badge[style*='--tone'] { border-color: color-mix(in srgb, var(--tone) 55%, transparent); color: var(--tone); }
.rw-row.on .rw-badge[style*='--tone'] { border-color: var(--tone); }

/* --- garage ------------------------------------------------------------ */

/* The fifth two-column layout. It has the strongest claim of any of them: the
   right-hand column is a live 3D car, and a car that has to share 660px with a
   list of forty items is not a preview. */
.rw-menu[data-screen='garage'] .rw-panel,
.rw-menu[data-screen='garageSlot'] .rw-panel,
.rw-menu[data-screen='garagePresets'] .rw-panel {
  max-width: 1220px;
  display: grid;
  grid-template-columns: minmax(320px, 440px) 1fr;
  grid-template-areas:
    'crumbs crumbs'
    'title  title'
    'sub    sub'
    'rows   extra'
    'note   note';
  align-content: start;
  column-gap: 34px;
}
.rw-menu[data-screen='garage'] .rw-crumbs,
.rw-menu[data-screen='garageSlot'] .rw-crumbs,
.rw-menu[data-screen='garagePresets'] .rw-crumbs { grid-area: crumbs; }
.rw-menu[data-screen='garage'] .rw-title,
.rw-menu[data-screen='garageSlot'] .rw-title,
.rw-menu[data-screen='garagePresets'] .rw-title { grid-area: title; font-size: clamp(30px, 3.4vw, 42px); }
.rw-menu[data-screen='garage'] .rw-sub,
.rw-menu[data-screen='garageSlot'] .rw-sub,
.rw-menu[data-screen='garagePresets'] .rw-sub { grid-area: sub; }
.rw-menu[data-screen='garage'] .rw-rows,
.rw-menu[data-screen='garageSlot'] .rw-rows,
.rw-menu[data-screen='garagePresets'] .rw-rows { grid-area: rows; max-height: 56vh; }
.rw-menu[data-screen='garage'] .rw-extra,
.rw-menu[data-screen='garageSlot'] .rw-extra,
.rw-menu[data-screen='garagePresets'] .rw-extra {
  grid-area: extra;
  margin-top: 22px;
  min-height: 0;
}
.rw-menu[data-screen='garage'] .rw-note,
.rw-menu[data-screen='garageSlot'] .rw-note,
.rw-menu[data-screen='garagePresets'] .rw-note { grid-area: note; }

/* The turntable. A hard-edged well with a faint floor wash under it, so the car
   reads as standing in a bay rather than floating over the menu. */
.rw-garage-preview {
  position: relative;
  height: clamp(240px, 42vh, 420px);
  border: 1px solid var(--rw-line);
  background:
    radial-gradient(60% 46% at 50% 82%, var(--rw-accent-dim) 0%, rgba(0, 0, 0, 0) 70%),
    linear-gradient(180deg, rgba(10, 14, 22, 0.9) 0%, rgba(4, 6, 11, 0.95) 100%);
  overflow: hidden;
}
.rw-garage-preview canvas { cursor: grab; }
.rw-garage-preview canvas:active { cursor: grabbing; }

/* --- front-screen showcase --------------------------------------------- */

/* The front screens' right half, which was empty air, holds the same turntable
   the garage uses — the car a match would actually put the player in. The
   two-column grid only engages when the showcase is present (:has), so a
   main screen without one (a live session exists) keeps the plain column.
   Both entry screens share it: welcome is three rows tall and would otherwise
   be a third of a screen of text with nothing beside it. */
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]),
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) {
  max-width: 1220px;
  display: grid;
  grid-template-columns: minmax(380px, 610px) 1fr;
  grid-template-areas:
    'crumbs extra'
    'title  extra'
    'sub    extra'
    'rows   extra'
    'note   extra';
  align-content: center;
  column-gap: clamp(34px, 5vw, 84px);
}
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-crumbs,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-crumbs { grid-area: crumbs; }
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-title,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-title { grid-area: title; }
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-sub,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-sub { grid-area: sub; }
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-rows,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-rows { grid-area: rows; }
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-note,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-note { grid-area: note; }
.rw-menu[data-screen='main'] .rw-panel:has([data-garage-preview]) .rw-extra,
.rw-menu[data-screen='welcome'] .rw-panel:has([data-garage-preview]) .rw-extra {
  grid-area: extra;
  margin-top: 0;
  align-self: center;
  min-height: 0;
}

/* Frameless, unlike the garage bay: on the front screen the car is scenery,
   not an inspection subject, so it stands in the open with only the floor
   wash beneath it. */
.rw-showcase.rw-garage-preview {
  height: clamp(300px, 56vh, 560px);
  border: none;
  background: radial-gradient(52% 34% at 50% 78%, var(--rw-accent-dim) 0%, rgba(0, 0, 0, 0) 72%);
}
.rw-showcase-tag {
  margin-top: 2px;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.5);
}

.rw-gbar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--rw-line);
  border: 1px solid var(--rw-line); border-top: none; }
.rw-gswatch { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: rgba(8, 11, 18, 0.8); }
.rw-gswatch i {
  width: 22px;
  height: 22px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  flex: none;
}
.rw-gswatch i.rw-gbody { background: linear-gradient(135deg, #2c3442 0%, #151a24 100%); }
.rw-gswatch span {
  display: block;
  font-size: 9px;
  font-weight: 650;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-gswatch b { display: block; margin-top: 3px; font-size: 13px; font-weight: 700; }

.rw-gchips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.rw-gchip {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 9px 4px;
  font-size: 11px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-left: 2px solid var(--tone, var(--rw-accent));
  background: rgba(8, 11, 18, 0.62);
}
.rw-gchip span {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.6);
}
.rw-gchip b { font-weight: 650; color: #e9f0ff; }
.rw-gchip i { width: 10px; height: 10px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.2); }

.rw-ghint {
  margin-top: 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.5);
}

/* A painted variant's own colour, beside the name. */
.rw-row-head { display: flex; align-items: center; gap: 9px; min-width: 0; }
.rw-dot {
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
}

.rw-gpresets { display: flex; flex-direction: column; gap: 1px; background: var(--rw-line);
  border: 1px solid var(--rw-line); }
.rw-gpreset {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 12px 14px;
  background: rgba(8, 11, 18, 0.8);
  font-size: 13px;
}
.rw-gpreset.on { border-left: 2px solid var(--rw-accent); padding-left: 12px; }
.rw-gpreset b { font-weight: 700; }
.rw-gcount { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; }
.rw-gcount[data-team='0'] { color: #6ea6ff; }
.rw-gcount[data-team='1'] { color: #ffab5c; }

/* Shop cards. The rarity is the only colour: it is the one thing a player
   reads a card for before the price. */
.rw-shop-head, .rw-chal-head {
  margin: 16px 0 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-shop-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}
.rw-shop-card {
  padding: 10px 12px 12px;
  background: rgba(8, 11, 18, 0.72);
  border: 1px solid var(--rw-line);
  border-top: 2px solid var(--rw-rarity, var(--rw-accent));
}
.rw-shop-card.owned { opacity: 0.5; }
.rw-shop-rarity {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--rw-rarity, var(--rw-accent));
}
.rw-shop-name {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
}
.rw-shop-price { margin-top: 8px; font-size: 13px; color: rgba(190, 208, 235, 0.8); }
.rw-shop-price b { font-weight: 750; color: #f2f7ff; }
.rw-shop-price s { color: rgba(160, 180, 210, 0.45); margin-right: 6px; }

/* The tier track. Two cells per column — free above premium — so the two
   tracks read as two rows the whole way along, as the real one does. */
.rw-tier-legend {
  display: flex;
  gap: 14px;
  margin: 14px 0 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.6);
}
.rw-tier-track { display: flex; gap: 4px; }
.rw-tier-col { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.rw-tier-cell {
  height: 26px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid transparent;
}
.rw-tier-cell.claimed { background: var(--rw-accent); opacity: 0.55; }
.rw-tier-cell.ready { background: var(--rw-accent-dim); border-color: var(--rw-accent); }
.rw-tier-cell.locked { background: rgba(255, 255, 255, 0.045); }
.rw-tier-cell.empty { background: rgba(255, 255, 255, 0.02); }
.rw-tier-num {
  text-align: center;
  font-size: 10px;
  font-weight: 650;
  color: rgba(150, 172, 205, 0.6);
}
.rw-tier-col.on .rw-tier-cell { border-color: #f2f7ff; }
.rw-tier-col.on .rw-tier-num { color: #f2f7ff; }
.rw-tier-col.pro .rw-tier-num { color: rgba(255, 202, 110, 0.85); }

.rw-tier-detail { margin-top: 16px; border-top: 1px solid var(--rw-line); padding-top: 12px; }
.rw-tier-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--rw-accent);
}
.rw-tier-line {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.rw-tier-line span {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--rw-muted);
}
.rw-tier-line.dim { opacity: 0.45; }
.rw-tier-line b { font-weight: 650; text-align: right; }

/* Said once, on the screen where it matters. */
.rw-earned {
  margin-top: 14px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(150, 172, 205, 0.62);
}

/* Challenge list. One grid template for every row, so the counters form a hard
   right edge rather than four independently-sized cells. */
.rw-chal {
  display: grid;
  grid-template-columns: 1fr 90px 62px 52px;
  gap: 10px;
  align-items: center;
  padding: 7px 4px;
  font-size: 13px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.rw-chal-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-chal-bar { height: 3px; background: rgba(255, 255, 255, 0.08); }
.rw-chal-bar i { display: block; height: 100%; background: var(--rw-accent); }
.rw-chal-count { text-align: right; font-size: 12px; color: rgba(190, 208, 235, 0.78); }
.rw-chal-tp {
  text-align: right;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: rgba(150, 172, 205, 0.6);
}
.rw-chal.ready .rw-chal-name { color: #4fd08a; }
.rw-chal.ready .rw-chal-bar i { background: #4fd08a; }
.rw-chal.claimed { opacity: 0.45; }
.rw-chal.expired { opacity: 0.4; }
.rw-chal.expired .rw-chal-count { color: #ff8f80; }

/* Full-time progression block. */
.rw-summary { margin-top: 18px; border-top: 1px solid var(--rw-line); padding-top: 12px; }
.rw-sum-head {
  margin: 10px 0 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.66);
}
.rw-sum-line {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.rw-sum-line span {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--rw-muted);
}
.rw-sum-line b { font-weight: 700; }
.rw-sum-line.up b { color: #4fd08a; }
.rw-sum-tag {
  display: inline-block;
  margin: 0 6px 6px 0;
  padding: 4px 8px 3px;
  font-size: 11px;
  font-weight: 650;
  background: rgba(255, 255, 255, 0.05);
  border-left: 2px solid var(--rw-accent);
}
.rw-sum-tag.drop { border-left-color: rgba(255, 202, 110, 0.85); }

@media (max-width: 1000px) {
  .rw-menu[data-screen='shop'] .rw-panel,
  .rw-menu[data-screen='rocketpass'] .rw-panel,
  .rw-menu[data-screen='challenges'] .rw-panel,
  .rw-menu[data-screen='ranked'] .rw-panel,
  .rw-menu[data-screen='garage'] .rw-panel,
  .rw-menu[data-screen='garageSlot'] .rw-panel,
  .rw-menu[data-screen='garagePresets'] .rw-panel {
    grid-template-columns: 1fr;
    grid-template-areas: 'crumbs' 'title' 'sub' 'extra' 'rows' 'note';
    max-width: 660px;
  }
  /* The preview goes ABOVE the rows on one column, not below: a car you have to
     scroll past the whole item list to see is not a live preview. */
  .rw-menu[data-screen='garage'] .rw-rows,
  .rw-menu[data-screen='garageSlot'] .rw-rows,
  .rw-menu[data-screen='garagePresets'] .rw-rows { max-height: none; }
}

/* --- legend ------------------------------------------------------------ */

.rw-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 20px;
  padding-top: 14px;
  border-top: 1px solid var(--rw-line);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.6);
}
.rw-legend em { font-style: normal; color: rgba(226, 236, 255, 0.9); letter-spacing: 0.1em; }
.rw-legend .gap { flex: 1; }

/* --- bind modal -------------------------------------------------------- */

.rw-modal {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 4, 8, 0.72);
  backdrop-filter: blur(3px);
}
.rw-modal[hidden] { display: none; }
.rw-modal-card {
  min-width: 380px;
  padding: 34px 40px 30px;
  text-align: center;
  background: rgba(9, 13, 22, 0.95);
  border: 1px solid var(--rw-line);
  border-top: 2px solid var(--rw-accent);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.6);
}
.rw-modal-action {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--rw-accent);
}
.rw-modal-title {
  margin-top: 10px;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.rw-modal-hint {
  margin-top: 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--rw-muted);
}

/* --- text rows and the typing prompt ----------------------------------- */

/* A free-text value is the one row whose content has no bound, so it is the one
   row allowed to ellipsize. It is also left-aligned inside a right-aligned
   value column, deliberately: the interesting end of a URL is the front. */
.rw-text {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: 13px;
  color: #e9f0ff;
}
.rw-text.empty { color: rgba(160, 180, 210, 0.4); font-style: italic; }
.rw-pen {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rw-accent);
  opacity: 0.45;
}
.rw-row.on .rw-pen { opacity: 1; }
.rw-row-value:has(.rw-text) { justify-self: stretch; overflow: hidden; }

/* The prompt has no <input> in it — see renderModal in ./menu.ts. The buffer is
   drawn as text and the caret is this block, blinking on the same 1.06 s cycle
   a real one does, so the fake is not the thing anybody notices. */
.rw-modal[data-kind='text'] .rw-modal-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 44px;
  font-size: 26px;
  letter-spacing: -0.01em;
  word-break: break-all;
}
.rw-typed { color: #f4f8ff; }
.rw-caret {
  display: inline-block;
  width: 2px;
  height: 30px;
  background: var(--rw-accent);
  animation: rw-blink 1.06s steps(1, end) infinite;
}
@keyframes rw-blink { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }
.rw-modal[data-kind='text'] .rw-modal-hint { text-transform: none; letter-spacing: 0.04em; }

/* --- multiplayer: lobby ------------------------------------------------- */

/* The lobby is the third two-column screen, for the same reason the training
   ones are: six roster rows beside six controls do not stack inside 660px. */
.rw-menu[data-screen='lobby'] .rw-panel,
.rw-menu[data-screen='connecting'] .rw-panel,
.rw-menu[data-screen='disconnected'] .rw-panel {
  max-width: 1060px;
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  grid-template-areas:
    'crumbs crumbs'
    'title  title'
    'sub    sub'
    'rows   extra'
    'note   note';
  align-content: start;
  column-gap: 30px;
}
.rw-menu[data-screen='lobby'] .rw-crumbs,
.rw-menu[data-screen='connecting'] .rw-crumbs,
.rw-menu[data-screen='disconnected'] .rw-crumbs { grid-area: crumbs; }
.rw-menu[data-screen='lobby'] .rw-title,
.rw-menu[data-screen='connecting'] .rw-title,
.rw-menu[data-screen='disconnected'] .rw-title { grid-area: title; }
.rw-menu[data-screen='lobby'] .rw-sub,
.rw-menu[data-screen='connecting'] .rw-sub,
.rw-menu[data-screen='disconnected'] .rw-sub { grid-area: sub; }
.rw-menu[data-screen='lobby'] .rw-rows,
.rw-menu[data-screen='connecting'] .rw-rows,
.rw-menu[data-screen='disconnected'] .rw-rows { grid-area: rows; }
.rw-menu[data-screen='lobby'] .rw-extra,
.rw-menu[data-screen='connecting'] .rw-extra,
.rw-menu[data-screen='disconnected'] .rw-extra {
  grid-area: extra;
  margin-top: 22px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.rw-menu[data-screen='lobby'] .rw-note,
.rw-menu[data-screen='connecting'] .rw-note,
.rw-menu[data-screen='disconnected'] .rw-note { grid-area: note; }

/* The join code: the one thing on this screen that gets read aloud. */
.rw-code {
  padding: 14px 18px 16px;
  background: rgba(8, 11, 18, 0.72);
  border: 1px solid var(--rw-line);
  border-left: 2px solid var(--rw-accent);
}
.rw-code-key {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--rw-muted);
}
.rw-code-value { display: flex; gap: 8px; margin-top: 8px; }
.rw-code-value b {
  min-width: 30px;
  padding: 4px 0 5px;
  text-align: center;
  font-size: 27px;
  font-weight: 800;
  letter-spacing: 0;
  color: #f6faff;
  background: rgba(255, 255, 255, 0.045);
  border-radius: 3px;
}
.rw-code.empty { border-left-color: rgba(255, 255, 255, 0.12); }
.rw-code-none { margin-top: 8px; font-size: 13px; color: var(--rw-muted); }

.rw-lobby-counts {
  display: flex;
  gap: 18px;
  margin-top: 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

/* One grid template for the header and every row, so the columns are a hard
   edge down the list rather than four independently-sized cells. */
.rw-lp {
  display: grid;
  grid-template-columns: 1fr 74px 78px 72px 62px;
  gap: 10px;
  align-items: center;
  padding: 7px 6px;
  font-size: 13px;
  font-weight: 620;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.rw-lp.head {
  margin-top: 12px;
  border-top: none;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.55);
}
.rw-lp.me { background: rgba(255, 255, 255, 0.05); border-radius: 4px; }
.rw-lp.bot { color: rgba(190, 208, 235, 0.5); }
.rw-lp-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-lp-name i {
  font-style: normal;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--rw-accent);
  margin-left: 8px;
}
.rw-lp-team, .rw-lp-car, .rw-lp-ready, .rw-lp-ping {
  font-size: 12px;
  color: rgba(186, 204, 232, 0.7);
}
.rw-lp-car { text-transform: capitalize; }
.rw-lp-ready.on { color: #4fd08a; }
.rw-lp-ping[data-band='good'] { color: #4fd08a; }
.rw-lp-ping[data-band='fair'] { color: #ffc75a; }
.rw-lp-ping[data-band='poor'] { color: #ff6b5a; }

/* --- multiplayer: connecting and disconnected -------------------------- */

.rw-connect { padding: 4px 2px; }
.rw-connect-line { font-size: 15px; font-weight: 650; }
.rw-q {
  position: relative;
  height: 3px;
  margin: 16px 0 18px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
}
.rw-q i { display: block; height: 100%; background: var(--rw-accent); transition: width 0.3s ease; }
/* A queue with no denominator has no progress to show, and a bar that pretends
   to have some is worse than one that admits it is only saying "still alive". */
.rw-q.indeterminate i { width: 34%; animation: rw-slide 1.4s ease-in-out infinite; }
@keyframes rw-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(295%); }
}
.rw-connect-host {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 7px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 12px;
}
.rw-connect-host span {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rw-muted);
}
.rw-connect-host b { font-weight: 650; word-break: break-all; text-align: right; }

.rw-dc { padding: 4px 2px; }
.rw-dc-code {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #ff8f80;
}
.rw-dc-text { margin-top: 10px; font-size: 16px; font-weight: 600; line-height: 1.45; }
.rw-dc-room { margin-top: 14px; font-size: 12px; color: var(--rw-muted); }

@media (max-height: 620px) {
  .rw-title { font-size: 34px; }
  .rw-row { min-height: 38px; }
  .rw-row.hero { min-height: 46px; }
  .rw-panel { padding: 10px 0; }
}

@media (max-width: 1000px) {
  .rw-menu[data-screen='lobby'] .rw-panel,
  .rw-menu[data-screen='connecting'] .rw-panel,
  .rw-menu[data-screen='disconnected'] .rw-panel {
    max-width: 660px;
    display: flex;
    flex-direction: column;
  }
}
`;
